import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MdOutlineAddCard } from "react-icons/md";
import { GrUpdate } from "react-icons/gr";
import axios from "axios";
import "./userList.css";

function UserDetails() {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [closeToggle, setCloseToggle] = useState(false); // default OFF
    const [showUpdatePaymentForm, setShowUpdatePaymentForm] = useState(false);
    const [payments, setPayments] = useState([]);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        userId: "",
        dueDate: "",
        paymentDate: "",
        amount: "",
        status: "Paid",
    });

    const [updateData, setUpdateData] = useState({
        userId: "",
        dueDate: "",
        paymentDate: "",
        amount: "",
        status: "Paid",
    });

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BASE_API_URL}/getUser/api/monthlyParkingUser/${id}`
                );

                setUser(response.data);

                // ✅ Assign IDs to both forms
                setFormData((prev) => ({
                    ...prev,
                    userId: response.data._id,
                }));
                setUpdateData((prev) => ({
                    ...prev,
                    userId: response.data._id,
                }));

                // ✅ Sync toggle with DB state:
                // - Closed → ON
                // - Closure requested → OFF (waiting for admin)
                // - Otherwise → OFF
                if (response.data.closed) {
                    setCloseToggle(true);
                } else {
                    setCloseToggle(false);
                }
            } catch (err) {
                setError(err.response ? err.response.data.message : err.message);
            } finally {
                setLoading(false);
            }
        };

        const fetchPaymentHistory = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BASE_API_URL}/payments/api/${id}/history`
                );
                setPayments(response.data.payments);
            } catch (err) {
                console.error("Failed to fetch payment history:", err);
            }
        };

        fetchUser();
        fetchPaymentHistory();
    }, [id]);

    const toggleUpdatePaymentForm = () => {
        setShowUpdatePaymentForm(!showUpdatePaymentForm);
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleUpdateChange = (e) => {
        const { id, value } = e.target;
        setUpdateData((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_API_URL}/payments/api/createMonthlyPayments`,
                formData,
                { headers: { "Content-Type": "application/json" } }
            );
            console.log("Payment submitted:", response.data);
            alert("Payment submitted successfully!");
        } catch (error) {
            console.error("Error submitting payment:", error);
            alert("Error submitting payment.");
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BASE_API_URL}/payments/api/updateMonthlyPayments`,
                updateData,
                { headers: { "Content-Type": "application/json" } }
            );
            console.log("Payment updated:", response.data);
            alert("Payment updated successfully!");
        } catch (error) {
            console.error("Error updating payment:", error);
            alert("Error updating payment.");
        }
    };

    // ✅ Closure toggle logic
    const handleClosureToggle = async (e) => {
        const isChecked = e.target.checked;

        try {
            if (isChecked && !user.closureRequest && !user.closed) {
                // First time requesting closure
                const res = await axios.put(
                    `${process.env.REACT_APP_BASE_API_URL}/monthlyParking/api/${user._id}/request-close`
                );
                setUser(res.data.data);
                setCloseToggle(true); // only ON after success
                alert("Closure request submitted!");
            } else if (isChecked && user.closureRequest && !user.closed) {
                // Admin confirms closure
                const res = await axios.put(
                    `${process.env.REACT_APP_BASE_API_URL}/monthlyRentalParking/api/closeMonthlyParkingRequest/${user._id}/close`
                );
                setUser(res.data.data);
                setCloseToggle(true);
                alert("Rental space closed successfully!");
            } else {
                // If user tries to uncheck (we don't allow)
                setCloseToggle(false);
                alert("Closure cannot be undone once requested.");
            }
        } catch (err) {
            console.error("Error handling closure:", err);
            setCloseToggle(false); // rollback
            alert("Something went wrong while closing the rental space.");
        }
    };

    return (
        <div className="aboutMonthlyUser container mt-4 mb-4">
            <button className="btn btn-secondary mb-3" onClick={() => navigate(-1)}>
                ⬅ Back
            </button>
            <h2>Monthly Rental User Details</h2>

            {loading && <p>Loading...</p>}
            {error && <p className="text-danger">Error: {error}</p>}

            {user && (
                <div className="userDetail row">
                    <div className="col-md-4 mb-3">
                        <div className="vehicleImage card p-3">
                            {user.closed ? (
                                <span
                                    className="badge bg-dark position-absolute top-0 end-0 m-2"
                                    title={`Closed on ${user.closedDate
                                            ? new Date(user.closedDate).toLocaleDateString()
                                            : "N/A"
                                        }`}
                                    style={{ fontSize: "0.8rem" }}
                                >
                                    ✅ Closed
                                </span>
                            ) : user.closureRequest ? (
                                <span
                                    className="badge bg-danger position-absolute top-0 end-0 m-2"
                                    title={`Closure Requested on ${user.closureRequestDate || "N/A"}`}
                                    style={{ fontSize: "0.8rem" }}
                                >
                                    🚫 Closing Requested
                                </span>
                            ) : null}

                            <img
                                src={`${process.env.REACT_APP_BASE_API_URL}/monthlyUploads/${user?.vehicleImage.split("\\").pop()}`}
                                alt="Vehicle"
                                className="img-fluid"
                            />
                        </div>

                    </div>

                    <div className="col-md-8">
                        <div className="userName card p-3">
                            <h4>Customer Name: {user.customerName.toUpperCase()}</h4>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-4 col-sm-6 mb-3">
                                <div className="card1 card p-3 text-center">
                                    <h5>Vehicle Number</h5>
                                    <p>{user.vehicleNumber.toUpperCase()}</p>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6 mb-3">
                                <div className="card2 card p-3 text-center">
                                    <h5>Vehicle Type</h5>
                                    <p>{user.vehicleType}</p>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6 mb-3">
                                <div className="card3 card p-3 text-center">
                                    <h5>Parking Slot</h5>
                                    <p>{user.parkingSlot}</p>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-4 col-sm-6 mb-3">
                                <div className="card4 card p-3 text-center">
                                    <h5>Rental Amount</h5>
                                    <p>Rs.{user.rentalAmount}/-</p>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6 mb-3">
                                <div className="card5 card p-3 text-center">
                                    <h5>Vehicle Entry Date</h5>
                                    <p>{new Date(user.createdAt).toLocaleDateString()}</p>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6 mb-3">
                                <div className="card6 card p-3 text-center">
                                    <h5>Customer Contact No</h5>
                                    <p>{user.contactNumber}</p>
                                    <h5>Customer Email Id</h5>
                                    <p>{user.email || "N/A"}</p>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-12 col-sm-6 mb-3">
                                <div className="card7 card p-3 text-center">
                                    <h4>Close the Rental Space</h4>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="flexSwitchCheckDefault"
                                            checked={closeToggle}
                                            disabled={user.closed} // disable only if permanently closed
                                            onChange={handleClosureToggle}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Section */}
                    <div className="paymentDetails mt-4">
                        <h3>Rental Payment Details</h3>
                        <div className="text-end mb-2">
                            <button
                                onClick={toggleUpdatePaymentForm}
                                className="btn btn-warning"
                                style={{ color: "#fff" }}
                            >
                                <GrUpdate style={{ height: "20px", width: "20px", marginBottom: "4px" }} />{" "}
                                {showUpdatePaymentForm ? "Close Form" : "Add Payment"}
                            </button>
                        </div>

                        {showUpdatePaymentForm && (
                            <div className="card p-3 mb-3">
                                <h5>Update Payment</h5>
                                <form onSubmit={handleUpdate}>
                                    <input type="hidden" id="userId" value={updateData.userId} />
                                    <div className="form-group text-start">
                                        <label htmlFor="dueDate">Due Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            id="dueDate"
                                            value={updateData.dueDate}
                                            onChange={handleUpdateChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group text-start">
                                        <label htmlFor="paymentDate">Payment Done Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            id="paymentDate"
                                            value={updateData.paymentDate}
                                            onChange={handleUpdateChange}
                                        />
                                    </div>
                                    <div className="form-group text-start">
                                        <label htmlFor="amount">Amount</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="amount"
                                            value={updateData.amount}
                                            onChange={handleUpdateChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group text-start">
                                        <label htmlFor="status">Status</label>
                                        <select
                                            className="form-control"
                                            id="status"
                                            value={updateData.status}
                                            onChange={handleUpdateChange}
                                        >
                                            <option value="Paid">Paid</option>
                                            <option value="Unpaid">Unpaid</option>
                                        </select>
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-warning mt-3"
                                        style={{ color: "#fff" }}
                                    >
                                        <GrUpdate style={{ height: "20px", width: "20px", marginBottom: "4px" }} />{" "}
                                        Update Payment
                                    </button>
                                </form>
                            </div>
                        )}

                        <table className="table table-bordered table-striped">
                            <thead>
                                <tr>
                                    <th>S.no</th>
                                    <th>Due Date</th>
                                    <th>Date of Payment</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center">
                                            No payment history found.
                                        </td>
                                    </tr>
                                ) : (
                                    payments.map((payment, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{new Date(payment.dueDate).toLocaleDateString()}</td>
                                            <td>
                                                {payment.paymentDate
                                                    ? new Date(payment.paymentDate).toLocaleDateString()
                                                    : "—"}
                                            </td>
                                            <td>&#8377;{payment.amount}</td>
                                            <td
                                                className={payment.status === "Paid" ? "text-success" : "text-danger"}
                                            >
                                                {payment.status}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserDetails;
